import { useCurrentAccount } from '@application/api';
import { DogBreederApplicationStatus } from '@gql/graphql';
import type { StepLineStep } from '@presentation/components';
import {
  Button,
  Card,
  Container,
  EmptyOverlay,
  InfoBox,
  Label,
  LoadingContainer,
  MtLayouTitleEditButtonContainer,
  MtLayoutTitleWrapper,
  SectionSubtitle,
  StepLine,
} from '@presentation/components';
import { MtLayouTitleBackButton } from '@presentation/components/MtLayout/styled';
import { COLOR_PER_DOG_BREEDER_APPLICATION_STATUS } from '@presentation/constants';
import { useTranslation } from '@presentation/utils';
import dayjs from 'dayjs';
import type { FC } from 'react';
import { useCallback, useMemo } from 'react';
import { FaChevronLeft } from 'react-icons/fa';
import { TbMessage } from 'react-icons/tb';
import { Link } from 'react-router-dom';
import * as Styled from './styled';
import type { MtApplicationPageProps } from './types';

export * from './types';

export const MtApplicationPage: FC<MtApplicationPageProps> = (props) => {
  const {
    application,
    loading,
    personType,
    onBackClick,
    onDeleteClick,
    onOwnClick,
    onPayClick,
    onMessagesClick,
    onCancelClick,
    status,
  } = props;
  const { translate } = useTranslation();
  const { currentAccount } = useCurrentAccount();
  const { translate: translateRessource } = useTranslation({
    keyPrefix: 'mt.applications',
  });

  const showStepLine = useMemo(() => {
    if (application) {
      return (
        application.status !== DogBreederApplicationStatus.CANCELED &&
        application.status !== DogBreederApplicationStatus.EXPIRED &&
        application.status !== DogBreederApplicationStatus.REFUNDED
      );
    }
    return false;
  }, [application]);

  const stepsSuccess: StepLineStep[] = useMemo(() => {
    const isDogBreeder = !!currentAccount?.dogBreeder;
    if (isDogBreeder) {
      return [
        {
          title: translateRessource('steps.breeder.one'),
        },
        {
          title: translateRessource('steps.breeder.two'),
        },
        {
          title: translateRessource('steps.breeder.three'),
          status: application?.status === DogBreederApplicationStatus.DONE ? 'finish' : undefined,
        },
      ];
    }
    return [
      { title: translateRessource('steps.buyer.one') },
      {
        title: translateRessource('steps.buyer.two'),
        status:
          application?.status === DogBreederApplicationStatus.CONTACT_AVAILABLE
            ? 'finish'
            : undefined,
      },
      {
        title: translateRessource('steps.buyer.three'),
        status: [
          DogBreederApplicationStatus.DONE,
          DogBreederApplicationStatus.WAITING_LIST,
        ].includes(application?.status)
          ? 'finish'
          : undefined,
      },
      {
        title: translateRessource('steps.buyer.four'),
        status: application?.status === DogBreederApplicationStatus.DONE ? 'finish' : undefined,
      },
    ];
  }, [application?.status, currentAccount?.dogBreeder, translateRessource]);

  const getCurrentStep = useCallback(() => {
    const isDogBreeder = !!currentAccount?.dogBreeder;
    if (isDogBreeder) {
      switch (application?.status) {
        case DogBreederApplicationStatus.AWAITING_PAYMENT:
          return 0;
        case DogBreederApplicationStatus.CONTACT_AVAILABLE:
          return 0;
        case DogBreederApplicationStatus.WAITING_LIST:
          return 1;
        case DogBreederApplicationStatus.DONE:
          return 2;
        default:
          return 1;
      }
    }

    switch (application?.status) {
      case DogBreederApplicationStatus.AWAITING_PAYMENT:
        return 1;
      case DogBreederApplicationStatus.CONTACT_AVAILABLE:
        return 2;
      case DogBreederApplicationStatus.WAITING_LIST:
        return 3;
      case DogBreederApplicationStatus.DONE:
        return 3;
      default:
        return 1;
    }
  }, [application?.status, currentAccount?.dogBreeder]);
  if (loading) return <LoadingContainer />;
  if (!application) return <EmptyOverlay />;

  const { breed, details, person, animal, status: applicationStatus } = application;

  const showContactDetails = [
    DogBreederApplicationStatus.CONTACT_AVAILABLE,
    DogBreederApplicationStatus.DONE,
    DogBreederApplicationStatus.WAITING_LIST,
  ].includes(applicationStatus);

  return (
    <Container>
      {showStepLine && (
        <Card>
          <StepLine currentStep={getCurrentStep()} steps={stepsSuccess} color="dark" />
        </Card>
      )}

      <MtLayouTitleBackButton onClick={onBackClick}>
        <FaChevronLeft />
        <div>{translate('common.buttons.back')}</div>
      </MtLayouTitleBackButton>

      <MtLayoutTitleWrapper>
        <div />
        <MtLayouTitleEditButtonContainer>
          {onDeleteClick && (
            <Styled.RejectButton onClick={onDeleteClick} color="red">
              <span>{translate('common.buttons.reject')}</span>
            </Styled.RejectButton>
          )}
          {onCancelClick && (
            <Styled.RejectButton onClick={onCancelClick} color="red">
              {translate('common.buttons.cancel')}
            </Styled.RejectButton>
          )}
          {onOwnClick && (
            <Button onClick={onOwnClick} color="blue">
              {translate(
                personType === 'breeder'
                  ? 'common.buttons.ownFromBreeder'
                  : 'common.buttons.ownFromBuyer',
              )}
            </Button>
          )}
          {onPayClick && (
            <Button onClick={onPayClick} color="blue">
              {translate('common.buttons.pay')}
            </Button>
          )}
        </MtLayouTitleEditButtonContainer>
      </MtLayoutTitleWrapper>

      {status && <InfoBox>{status}</InfoBox>}

      {showContactDetails && (
        <>
          <Styled.TitleContainer>
            <SectionSubtitle>
              {translate('glossary.contactPerson').replace(
                '$type',
                translate(`glossary.oppositePersonType.${personType}`),
              )}
            </SectionSubtitle>
          </Styled.TitleContainer>
          <Styled.ContactContainer>
            <Styled.ItemImg src={person.image} alt={person.title} />

            <Styled.ContactRow>
              <div>
                <Styled.ItemWrapperLabel>{translate('glossary.name')}</Styled.ItemWrapperLabel>
                <Styled.ItemWrapperValue>
                  {person.link ? (
                    <Link to={person.link} target="_blank" rel="noopener noreferrer">
                      {person.title}
                    </Link>
                  ) : (
                    <div>{person.title}</div>
                  )}
                </Styled.ItemWrapperValue>
              </div>

              {person.agreementNumber ? (
                <div>
                  <Styled.ItemWrapperLabel>
                    {translate(`glossary.agreementNumber.${person.country ?? 'BE'}`)}
                  </Styled.ItemWrapperLabel>
                  <Styled.ItemWrapperValue>{person.agreementNumber}</Styled.ItemWrapperValue>
                </div>
              ) : (
                <div />
              )}

              <div>
                <Styled.ItemWrapperLabel>
                  {translate('glossary.phoneNumber')}
                </Styled.ItemWrapperLabel>
                <Styled.ItemWrapperValue>{person.phoneNumber}</Styled.ItemWrapperValue>
              </div>

              <div>
                <Styled.ItemWrapperLabel>{translate('glossary.email')}</Styled.ItemWrapperLabel>
                <Styled.ItemWrapperValue>{person.email}</Styled.ItemWrapperValue>
              </div>
            </Styled.ContactRow>

            {onMessagesClick && (
              <Button
                onClick={onMessagesClick}
                outlined
                color="blue"
                iconLeft={<TbMessage />}
                shrink
              >
                {translate('common.buttons.sendMessage')}
              </Button>
            )}
          </Styled.ContactContainer>
        </>
      )}

      <Styled.TitleContainer>
        <SectionSubtitle>{translate('glossary.quizAnswers')}</SectionSubtitle>
      </Styled.TitleContainer>
      <Styled.ColumnContainer>
        <Styled.ColumnWrapper>
          {details.map((group, groupIndex) => (
            <Styled.DetailGroupWrapper key={groupIndex}>
              <Styled.DetailGroupLabel>{group.label}</Styled.DetailGroupLabel>

              {group.value.map((detail, index) => (
                <Styled.DetailWrapper key={index}>
                  <Styled.DetailLabel>{detail.label}</Styled.DetailLabel>
                  <Styled.DetailValue dangerouslySetInnerHTML={{ __html: detail.value }} />
                </Styled.DetailWrapper>
              ))}
            </Styled.DetailGroupWrapper>
          ))}
        </Styled.ColumnWrapper>

        <Styled.ColumnWrapper>
          <Styled.ItemContainer>
            <Styled.ItemWrapper>
              <Styled.ItemWrapperRow alignCenter>
                <Styled.ItemImg src={breed.image} alt={breed.title} />
                <div>{breed.title}</div>
              </Styled.ItemWrapperRow>

              <Styled.ItemWrapperRow marginTop={16}>
                <Styled.ItemWrapperCol>
                  <Styled.ItemWrapperLabel>{translate('glossary.status')}</Styled.ItemWrapperLabel>
                  <Styled.ItemWrapperValue>
                    <Label
                      theme={COLOR_PER_DOG_BREEDER_APPLICATION_STATUS[application.status]}
                      content={translate(`enum.dogBreederApplicationStatus.${application.status}`)}
                      size="small"
                    />
                  </Styled.ItemWrapperValue>
                </Styled.ItemWrapperCol>

                <Styled.ItemWrapperCol>
                  <Styled.ItemWrapperLabel>
                    {translate('glossary.createdAt')}
                  </Styled.ItemWrapperLabel>
                  <Styled.ItemWrapperValue>
                    <span>{dayjs(application.createdAt).format('DD/MM/YYYY')}</span>
                  </Styled.ItemWrapperValue>
                </Styled.ItemWrapperCol>
              </Styled.ItemWrapperRow>

              {animal && (
                <Styled.ItemWrapperRow marginTop={16}>
                  <Styled.ItemWrapperCol>
                    <Styled.ItemWrapperLabel>
                      {translate('glossary.puppyName')}
                    </Styled.ItemWrapperLabel>
                    <Styled.ItemWrapperValue>{animal.name ?? '-'}</Styled.ItemWrapperValue>
                  </Styled.ItemWrapperCol>

                  <Styled.ItemWrapperCol>
                    <Styled.ItemWrapperLabel>
                      {translate('glossary.puppyBirthdate')}
                    </Styled.ItemWrapperLabel>
                    <Styled.ItemWrapperValue>
                      <span>
                        {animal.dateOfBirth ? dayjs(animal.dateOfBirth).format('DD/MM/YYYY') : '-'}
                      </span>
                    </Styled.ItemWrapperValue>
                  </Styled.ItemWrapperCol>
                </Styled.ItemWrapperRow>
              )}
            </Styled.ItemWrapper>

            {!showContactDetails && (
              <Styled.ItemWrapper withBorder>
                <Styled.ItemWrapperRow marginTop={16} alignCenter>
                  <Styled.ItemImg src={person.image} alt={person.title} />
                  {person.link ? (
                    <Link to={person.link} target="_blank" rel="noopener noreferrer">
                      {person.title}
                    </Link>
                  ) : (
                    <div>{person.title}</div>
                  )}
                </Styled.ItemWrapperRow>

                {person.phoneNumber && (
                  <Styled.ItemWrapperRow marginTop={16}>
                    <Styled.ItemWrapperCol width={100}>
                      <Styled.ItemWrapperLabel>
                        {translate('glossary.phoneNumber')}
                      </Styled.ItemWrapperLabel>
                      <Styled.ItemWrapperValue>{person.phoneNumber}</Styled.ItemWrapperValue>
                    </Styled.ItemWrapperCol>
                  </Styled.ItemWrapperRow>
                )}

                {person.email && (
                  <Styled.ItemWrapperRow marginTop={16}>
                    <Styled.ItemWrapperCol width={100}>
                      <Styled.ItemWrapperLabel>
                        {translate('glossary.email')}
                      </Styled.ItemWrapperLabel>
                      <Styled.ItemWrapperValue>{person.email}</Styled.ItemWrapperValue>
                    </Styled.ItemWrapperCol>
                  </Styled.ItemWrapperRow>
                )}
              </Styled.ItemWrapper>
            )}
          </Styled.ItemContainer>

          {application.differentCountryWarning && (
            <InfoBox warning="true">
              <div
                // eslint-disable-next-line react/no-danger
                dangerouslySetInnerHTML={{
                  __html: application.differentCountryWarning,
                }}
              />
            </InfoBox>
          )}
        </Styled.ColumnWrapper>
      </Styled.ColumnContainer>
    </Container>
  );
};
